/**
 * @file The "shop the look" strip and its dialog (sections/instagram-look.liquid).
 *
 * Two elements, one feature:
 * - <bk-look-strip> holds the header, the previous/next buttons and the scroll-snap list. It scrolls
 *   by one tile, keeps the arrows disabled at both ends, follows the document direction (so the
 *   arrows stay right in right-to-left), and opens the dialog for the tile that was activated.
 * - <bk-look-dialog> extends BkDialog. Every tile already has a server-rendered panel inside the one
 *   <dialog>, so prices are never templated in the browser. openView() shows the panel of the
 *   requested tile and names the dialog after that panel's heading. A linked reel sits in a
 *   <bk-reel-player> (assets/reel-player.js): it plays once its panel is shown, never under reduced
 *   motion, keeps a real pause toggle, and every video is paused on close. Focus returns to the tile.
 * - A reel panel without an uploaded video may carry a play button [data-ig-inline="<post URL>"] on
 *   its photo, inside [data-look-media]. Pressing it swaps the photo for Instagram's own embed player
 *   (createIgFrame, assets/ig-embed.js) and moves focus to the player; the photo comes back when the
 *   dialog closes, whichever way it closes, which also stops the video.
 */
import { prefersReducedMotion } from '@theme/utils';
import { BkDialog } from '@theme/dialog';
import { createIgFrame } from '@theme/ig-embed';

class BkLookDialog extends BkDialog {
  /** @type {AbortController | null} */
  #controller = null;

  /**
   * Media well currently showing Instagram's player, with the nodes it replaced.
   * @type {{ media: HTMLElement, nodes: Node[] } | null}
   */
  #swapped = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', (event) => this.#handleInlinePlay(event), { signal });
    // "close" fires for every way of closing (button, Esc, backdrop, close()).
    this.querySelector('dialog')?.addEventListener('close', () => this.#restoreMedia(), { signal });
  }

  /**
   * Show one tile's panel and open the dialog.
   * @param {string} viewId - Block id of the tile
   * @param {HTMLElement | null} [trigger] - Tile control that was activated
   */
  openView(viewId, trigger) {
    this.#restoreMedia();

    /** @type {HTMLElement | null} */
    let active = null;
    for (const view of this.querySelectorAll('[data-look-view]')) {
      if (!(view instanceof HTMLElement)) continue;
      const matches = view.dataset.lookView === viewId;
      // Built the first time this look is opened (see the <template> in sections/instagram-look).
      if (matches) {
        const template = view.querySelector('template[data-look-content]');
        if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
      }
      view.hidden = !matches;
      if (matches) active = view;
    }
    if (!active) return;

    const dialog = this.querySelector('dialog');
    const title = active.querySelector('[data-look-title]');
    if (dialog && title?.id) dialog.setAttribute('aria-labelledby', title.id);

    this.open(trigger);
    active.scrollTop = 0;
  }

  close() {
    this.#pauseVideos();
    super.close();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#restoreMedia();
    this.#pauseVideos();
    super.disconnectedCallback();
  }

  #pauseVideos() {
    for (const video of this.querySelectorAll('video')) video.pause();
  }

  /** @param {MouseEvent} event */
  #handleInlinePlay(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-ig-inline]');
    if (!(button instanceof HTMLElement)) return;
    const media = button.closest('[data-look-media]');
    if (!(media instanceof HTMLElement)) return;

    event.preventDefault();
    const url = button.dataset.igInline ?? '';
    const frame = createIgFrame(url, button.dataset.igEmbedTitle ?? '');
    if (!frame) {
      if (url) window.open(url, '_blank', 'noopener');
      return;
    }

    this.#restoreMedia();
    this.#swapped = { media, nodes: [...media.childNodes] };
    media.replaceChildren(frame);
    media.toggleAttribute('data-ig-playing', true);
    // The pressed button is gone: keep focus inside the dialog, on the player.
    frame.focus();
  }

  #restoreMedia() {
    const swapped = this.#swapped;
    if (!swapped) return;
    this.#swapped = null;
    swapped.media.replaceChildren(...swapped.nodes);
    swapped.media.removeAttribute('data-ig-playing');
  }
}

class BkLookStrip extends HTMLElement {
  /** @type {HTMLElement | null} */
  #list = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    const list = this.querySelector('[data-look-list]');
    const prev = this.querySelector('[data-look-prev]');
    const next = this.querySelector('[data-look-next]');
    this.#list = list instanceof HTMLElement ? list : null;
    this.#prev = prev instanceof HTMLButtonElement ? prev : null;
    this.#next = next instanceof HTMLButtonElement ? next : null;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.#list?.addEventListener('scroll', () => this.#syncArrows(), { passive: true, signal });
    document.addEventListener('shopify:block:select', (event) => this.#handleBlockSelect(event), { signal });

    if (this.#list) {
      this.#resizeObserver = new ResizeObserver(() => this.#syncArrows());
      this.#resizeObserver.observe(this.#list);
    }
    this.#syncArrows();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** @param {Event} event */
  #handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-look-open]');
    if (opener instanceof HTMLElement) {
      const dialog = this.#dialog();
      if (dialog) {
        event.preventDefault();
        dialog.openView(opener.dataset.lookOpen ?? '', opener);
      }
      return;
    }

    if (target.closest('[data-look-prev]')) this.#scrollByTile(-1);
    else if (target.closest('[data-look-next]')) this.#scrollByTile(1);
  }

  /** @returns {BkLookDialog | null} */
  #dialog() {
    const id = this.dataset.lookDialog;
    const found = id ? document.getElementById(id) : null;
    return found instanceof BkLookDialog ? found : null;
  }

  /** @param {number} direction - -1 previous, 1 next */
  #scrollByTile(direction) {
    const list = this.#list;
    if (!list) return;

    const item = list.firstElementChild;
    const step = item instanceof HTMLElement ? item.offsetWidth + this.#gap(list) : list.clientWidth;
    const sign = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
    list.scrollBy({
      left: step * direction * sign,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /**
   * @param {HTMLElement} list
   * @returns {number}
   */
  #gap(list) {
    const value = Number.parseFloat(getComputedStyle(list).columnGap);
    return Number.isFinite(value) ? value : 0;
  }

  #syncArrows() {
    const list = this.#list;
    if (!list || (!this.#prev && !this.#next)) return;

    // scrollLeft is 0 at the start and negative towards the end in right-to-left: compare magnitudes.
    const position = Math.abs(list.scrollLeft);
    const max = list.scrollWidth - list.clientWidth;
    const scrollable = max > 1;
    this.toggleAttribute('data-scrollable', scrollable);
    if (this.#prev) this.#prev.disabled = !scrollable || position <= 1;
    if (this.#next) this.#next.disabled = !scrollable || position >= max - 1;
  }

  /** @param {Event} event */
  #handleBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    target.scrollIntoView({ block: 'nearest', inline: 'center' });
  }
}

if (!customElements.get('bk-look-dialog')) customElements.define('bk-look-dialog', BkLookDialog);
if (!customElements.get('bk-look-strip')) customElements.define('bk-look-strip', BkLookStrip);
